import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Search, Menu, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { NAV_LINKS } from "@/constants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  const isActive = (href) => {
    if (href === "/blog") {
      return location.pathname === "/blog";
    }
    if (href.startsWith("/#")) {
      const sectionId = href.substring(2); // e.g. "mission" from "/#mission"
      if (location.pathname === "/") {
        if (activeSection) {
          return activeSection === sectionId;
        }
        const hash = href.substring(1);
        return location.hash === hash;
      }
    }
    return false;
  };

  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection("");
      return;
    }

    const handleScroll = () => {
      const scrollPosition = window.scrollY;

      if (scrollPosition < 150) {
        setActiveSection("");
        return;
      }

      const sections = ["mission", "solutions", "why", "faq", "nri-fixed-deposits"];
      let currentSection = "";

      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const top = el.offsetTop - 250;
          const bottom = top + el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < bottom) {
            currentSection = id;
          }
        }
      }

      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener("scroll", handleScroll);
    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onAuthCta = (path) => () => {
    setMobileOpen(false);
    navigate(path);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 border-b transition-all duration-500 ${
        scrolled
          ? "bg-[hsl(42_38%_97%/0.96)] backdrop-blur-xl border-[hsl(38_25%_85%)] shadow-[0_1px_12px_hsl(38_30%_60%/0.12)]"
          : "bg-[hsl(42_38%_97%/0.82)] backdrop-blur-md border-[hsl(38_25%_88%/0.6)]"
      }`}
    >
      <div
        className={`max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between transition-all duration-500 ${
          scrolled ? "h-[76px] md:h-[88px]" : "h-24 md:h-28"
        }`}
      >
        <Logo
          size={scrolled ? "h-14 md:h-16" : "h-[72px] md:h-20"}
          to="/"
          className="transition-transform duration-300 hover:scale-[1.02]"
        />

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => {
            const active = isActive(l.href);
            return (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={(e) => {
                  if (l.href.startsWith("/#")) {
                    e.preventDefault();
                    const sectionId = l.href.substring(2);
                    if (location.pathname === "/") {
                      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
                    } else {
                      navigate("/");
                      setTimeout(() => {
                        document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
                      }, 300);
                    }
                  }
                }}
                className={`relative font-heading text-[13.5px] tracking-tight transition-colors duration-200 py-2 group font-medium ${
                  active 
                    ? "text-[hsl(var(--zt-gold))]" 
                    : "text-[hsl(var(--zt-charcoal))] hover:text-[hsl(var(--zt-gold))]"
                }`}
              >
                {l.label}
                {/* Elegant expanding underline */}
                <span className={`absolute -bottom-0.5 left-0 h-[2px] rounded-full bg-[hsl(var(--zt-gold))] transition-all duration-200 group-hover:w-full ${
                  active ? "w-full" : "w-0"
                }`} />
              </a>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <button
            type="button"
            data-testid="search-button"
            aria-label="Search"
            className="w-10 h-10 rounded-xl grid place-items-center text-[hsl(var(--zt-charcoal))] hover:bg-[hsl(var(--zt-beige))] hover:text-[hsl(var(--zt-teal))] transition-colors duration-200"
          >
            <Search size={17} />
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="user-menu-trigger"
                  variant="ghost"
                  className="font-heading text-[13.5px] font-medium tracking-tight text-[hsl(var(--zt-teal))] hover:bg-[hsl(var(--zt-beige))] hover:text-[hsl(var(--zt-teal))] h-11 px-5 border border-[hsl(var(--zt-beige-deep))] rounded-xl transition-all duration-200"
                >
                  {user.name?.split(" ")[0] || user.email}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 mt-2 border border-[hsl(var(--zt-beige-deep))] rounded-xl shadow-lg">
                <DropdownMenuLabel className="font-heading text-[11.5px] font-semibold text-[hsl(var(--zt-muted))] px-3 py-2">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="font-heading text-[13px] font-medium px-3 py-2.5 cursor-pointer focus:bg-[hsl(var(--zt-beige))] focus:text-[hsl(var(--zt-charcoal))]" data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}>
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem className="font-heading text-[13px] font-medium px-3 py-2.5 cursor-pointer focus:bg-[hsl(var(--zt-beige))] focus:text-[hsl(var(--zt-charcoal))]" onClick={() => navigate("/profile")}>
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem className="font-heading text-[13px] font-medium px-3 py-2.5 cursor-pointer focus:bg-[hsl(var(--zt-beige))] focus:text-[hsl(var(--zt-charcoal))]" onClick={() => navigate("/portfolio")}>
                  Portfolio
                </DropdownMenuItem>
                <DropdownMenuItem className="font-heading text-[13px] font-medium px-3 py-2.5 cursor-pointer focus:bg-[hsl(var(--zt-beige))] focus:text-[hsl(var(--zt-charcoal))]" onClick={() => navigate("/appointments")}>
                  My Appointments
                </DropdownMenuItem>
                <DropdownMenuItem className="font-heading text-[13px] font-medium px-3 py-2.5 cursor-pointer focus:bg-[hsl(var(--zt-beige))] focus:text-[hsl(var(--zt-charcoal))]" onClick={() => navigate("/documents")}>
                  KYC Documents
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem className="font-heading text-[13px] font-medium px-3 py-2.5 cursor-pointer focus:bg-[hsl(var(--zt-beige))] text-[hsl(var(--zt-gold))]" onClick={() => navigate("/admin")}>
                    Admin Panel
                  </DropdownMenuItem>
                )}
                {user?.role === "advisor" && (
                  <DropdownMenuItem className="font-heading text-[13px] font-medium px-3 py-2.5 cursor-pointer focus:bg-[hsl(var(--zt-beige))] text-[hsl(var(--zt-gold))]" onClick={() => navigate("/advisor")}>
                    Advisor Portal
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="font-heading text-[13px] font-medium px-3 py-2.5 cursor-pointer text-destructive focus:bg-destructive/5 focus:text-destructive"
                  data-testid="menu-logout"
                  onClick={async () => { await logout(); navigate("/"); }}
                >
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                onClick={onAuthCta("/login")}
                data-testid="login-cta"
                variant="ghost"
                className="font-heading text-[13.5px] text-[hsl(var(--zt-charcoal))] hover:text-[hsl(var(--zt-teal))] hover:bg-[hsl(var(--zt-beige))] h-11 px-5 rounded-xl font-medium tracking-tight transition-all duration-200"
              >
                Log in
              </Button>
              <Button
                onClick={onAuthCta("/signup")}
                data-testid="signup-cta"
                className="font-heading text-[13.5px] bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/92 rounded-xl h-11 px-6 font-semibold tracking-tight elev-2 transition-all duration-200 hover:-translate-y-0.5"
              >
                Register
              </Button>
            </>
          )}
        </div>

        <button
          type="button"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden w-11 h-11 grid place-items-center text-[hsl(var(--zt-teal))] rounded-xl hover:bg-[hsl(var(--zt-beige))] transition-colors"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-[hsl(var(--zt-beige-deep))] px-6 py-7 space-y-4 animate-fade-up">
          {NAV_LINKS.map((l) => {
            const active = isActive(l.href);
            return (
              <a
                key={l.label}
                href={l.href}
                onClick={(e) => {
                  setMobileOpen(false);
                  if (l.href.startsWith("/#")) {
                    e.preventDefault();
                    const sectionId = l.href.substring(2);
                    if (location.pathname === "/") {
                      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
                    } else {
                      navigate("/");
                      setTimeout(() => {
                        document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
                      }, 300);
                    }
                  }
                }}
                className={`block font-heading text-[14px] font-medium py-1.5 transition-colors ${
                  active ? "text-[hsl(var(--zt-gold))]" : "text-[hsl(var(--zt-charcoal))]"
                }`}
              >
                {l.label}
              </a>
            );
          })}
          <div className="flex gap-3 pt-4 border-t border-[hsl(var(--zt-beige-deep))]">
            {user ? (
              <Button onClick={() => navigate("/dashboard")} className="flex-1 bg-[hsl(var(--zt-teal))] text-white rounded-xl h-12 font-heading text-[13.5px] font-semibold">
                Dashboard
              </Button>
            ) : (
              <>
                <Button onClick={onAuthCta("/login")} variant="outline" className="flex-1 rounded-xl border-[hsl(var(--zt-beige-deep))] h-12 font-heading text-[13.5px] font-medium">Log in</Button>
                <Button onClick={onAuthCta("/signup")} className="flex-1 bg-[hsl(var(--zt-gold))] text-white rounded-xl h-12 font-heading text-[13.5px] font-semibold">Register</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
